import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

/**
 * TasksContent — renders the tasks overlay inside the launcher's panel.
 *
 * THREE task types merged into one list:
 *   - Assignment tasks: derived from card_assignments (external). Click
 *     navigates to the pipeline card. Cannot be marked done here —
 *     unassign yourself from the card UI to clear.
 *   - Custom tasks: user-created via + New. Done button available.
 *   - Outreach tasks: from outreach campaigns' sequenced steps. Click
 *     navigates to /outreach (Tasks tab). Done button available and
 *     hits /api/outreach/tasks/:id/complete directly (server-side that
 *     endpoint advances the sequence to the next step).
 *
 * Each type has a distinct colored tag: Assigned = blue, Task = emerald,
 * Outreach = amber.
 *
 * After any create/complete, we broadcast `banyak:tasks_updated` so
 * the shell refreshes its badge count.
 */
export default function TasksContent() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState(null);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } catch (e) {
      console.error('Failed to load tasks:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const broadcastUpdate = () => {
    window.dispatchEvent(new CustomEvent('banyak:tasks_updated'));
  };

  const handleTaskClick = (task) => {
    if (task.task_type === 'assignment' && task.card_id && task.pipeline_id) {
      // Route to the search's internal pipeline (kanban view) with
      // ?card={cardId} — PipelineBoard picks that up and auto-opens
      // the CandidatePreviewPanel for that specific card. This puts
      // the user in the right search context (internal pipeline
      // sidebar set to this search) with the candidate already open.
      navigate(`/search/${task.pipeline_id}?tab=pipeline&sub=kanban&card=${task.card_id}`);
    } else if (task.task_type === 'outreach') {
      // No per-task detail; route to the outreach tab that shows tasks.
      navigate('/outreach?tab=tasks');
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks`, {
        method: 'POST', headers,
        body: JSON.stringify({ title: newTitle, description: newDesc }),
      });
      if (res.ok) {
        setNewTitle(''); setNewDesc(''); setShowNewForm(false);
        await load();
        broadcastUpdate();
      }
    } catch (e) {
      console.error('Failed to create task:', e);
    } finally {
      setCreating(false);
    }
  };

  const handleComplete = async (task) => {
    setCompleting(task.id);
    try {
      let res;
      if (task.task_type === 'custom') {
        const rawId = task.id.startsWith('c') ? task.id.substring(1) : task.id;
        res = await fetch(`${API_BASE_URL}/api/tasks/custom/${rawId}/complete`, {
          method: 'PUT', headers,
        });
      } else if (task.task_type === 'outreach') {
        // Delegate to the existing outreach endpoint which also advances
        // the campaign sequence to the next step server-side.
        const rawId = task.outreach_task_id || (task.id.startsWith('o') ? task.id.substring(1) : task.id);
        res = await fetch(`${API_BASE_URL}/api/outreach/tasks/${rawId}/complete`, {
          method: 'PUT', headers,
        });
      } else if (task.task_type === 'reminder') {
        // Reminders are marked done on the reminders table (not tasks).
        const rawId = task.reminder_id || (task.id.startsWith('r') ? task.id.substring(1) : task.id);
        res = await fetch(`${API_BASE_URL}/api/reminders/${rawId}/complete`, {
          method: 'PUT', headers,
        });
      } else {
        return; // assignment tasks are dismissed by unassigning
      }
      if (res && res.ok) {
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
        broadcastUpdate();
      }
    } catch (e) {
      console.error('Failed to complete task:', e);
    } finally {
      setCompleting(null);
    }
  };

  // Per-type tag styling and label
  const tagFor = (type) => {
    switch (type) {
      case 'assignment': return { label: 'Assigned', cls: 'bg-blue-100 text-blue-700' };
      case 'custom':     return { label: 'Task',     cls: 'bg-emerald-100 text-emerald-700' };
      case 'outreach':   return { label: 'Outreach', cls: 'bg-amber-100 text-amber-700' };
      case 'reminder':   return { label: 'REMINDER', cls: 'bg-orange-100 text-orange-700' };
      default:           return { label: 'Task',     cls: 'bg-gray-100 text-gray-700' };
    }
  };

  // Short-form relative date for the "Created X ago" line on each row.
  // Falls back to a short absolute date once the task is more than a week old.
  const formatRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    const seconds = Math.floor((Date.now() - then) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    const days = Math.floor(seconds / 86400);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: days > 365 ? 'numeric' : undefined });
  };

  return (
    <div className="absolute inset-0 z-20 bg-white flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 flex items-center gap-2">
        <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Tasks</span>
        <button
          onClick={() => setShowNewForm((v) => !v)}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors text-xs font-medium"
          title="New task"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New</span>
        </button>
      </div>

      {/* Inline create form */}
      {showNewForm && (
        <div className="flex-shrink-0 p-3 border-b border-gray-100 bg-gray-50 space-y-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Task title..."
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-emerald-500"
            autoFocus
          />
          <textarea
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-emerald-500 resize-none"
          />
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setShowNewForm(false); setNewTitle(''); setNewDesc(''); }}
              className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
            >Cancel</button>
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || creating}
              className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >{creating ? 'Creating...' : 'Create'}</button>
          </div>
        </div>
      )}

      {/* Task list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No tasks. Click <span className="font-medium">+ New</span> above to create one.
          </div>
        ) : (
          <div className="p-2 space-y-1.5">
            {tasks.map((task) => {
              const tag = tagFor(task.task_type);
              return (
                <div
                  key={task.id}
                  className="p-2.5 border border-gray-200 rounded-lg flex items-start gap-2.5 hover:border-gray-300 transition-colors"
                >
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => handleTaskClick(task)}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold ${tag.cls}`}>
                        {tag.label}
                      </span>
                      <span className="text-sm font-medium text-gray-900 truncate">{task.title}</span>
                    </div>
                    {task.task_type === 'assignment' && task.pipeline_name && (
                      <div className="text-xs text-gray-500">in {task.pipeline_name}</div>
                    )}
                    {task.task_type === 'outreach' && (
                      <div className="text-xs text-gray-500 flex items-center gap-1.5">
                        {task.candidate_name && <span className="font-medium">{task.candidate_name}</span>}
                        {task.candidate_name && task.campaign_name && <span>·</span>}
                        {task.campaign_name && <span>{task.campaign_name}</span>}
                      </div>
                    )}
                    {task.task_type === 'custom' && task.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{task.description}</div>
                    )}
                    {task.task_type === 'reminder' && task.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{task.description}</div>
                    )}
                    {/* Original creation date — shown for every task type
                        so recruiters have context on how long a task has
                        been sitting in their list. Falls back to "" for
                        rows missing created_at, avoiding "Created" alone. */}
                    {task.created_at && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        Created {formatRelative(task.created_at)}
                      </div>
                    )}
                  </div>
                  {(task.task_type === 'custom' || task.task_type === 'outreach' || task.task_type === 'reminder') && (
                    <button
                      onClick={() => handleComplete(task)}
                      disabled={completing === task.id}
                      className="flex-shrink-0 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50"
                    >
                      {completing === task.id ? '...' : 'Done'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
